import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  LinearProgress,
  Menu,
  alpha,
  useTheme,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import AddIcon from '@mui/icons-material/Add';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import FilterListIcon from '@mui/icons-material/FilterList';
import GrassIcon from '@mui/icons-material/Grass';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpacityIcon from '@mui/icons-material/Opacity';
import PaidIcon from '@mui/icons-material/Paid';
import SearchIcon from '@mui/icons-material/Search';
import TerrainIcon from '@mui/icons-material/Terrain';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { styled } from '@mui/material/styles';
import AppAlert from '../../common/AppAlert';
import EmptyState from '../../common/EmptyState';
import LoadingState from '../../common/LoadingState';
import { supabase } from '../../../services/supabaseClient';
import dayjs from 'dayjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

interface Crop {
  id: string;
  name: string;
  variety?: string;
  plantingDate: string;
  harvestDate: string;
  area: number;
  status: 'planted' | 'growing' | 'ready-to-harvest' | 'harvested' | 'failed';
  notes: string;
  expectedYield?: number;
  yieldUnit?: string;
  marketPrice?: number;
  productionCost?: number;
}

interface FarmCropsProps {
  farm: any;
}

const cropTypes = [
  'Cereals',
  'Legumes',
  'Vegetables',
  'Fruits',
  'Root Crops',
  'Cash Crops',
  'Fodder Crops',
];

const cropStatuses = [
  'planted',
  'growing',
  'ready-to-harvest',
  'harvested',
  'failed',
];

const yieldUnits = [
  'kg',
  'tonnes',
  '90kg bag',
  '50kg bag',
  'crate',
  'liters',
  'bunches',
  'pieces'
];

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
  borderRadius: theme.shape.borderRadius * 2,
}));

const StatusChip = styled(Chip)<{ status: string }>(({ theme, status }) => {
  let color = theme.palette.primary.main;
  let bg = alpha(theme.palette.primary.main, 0.1);

  switch (status) {
    case 'growing':
      color = theme.palette.info.main;
      bg = alpha(theme.palette.info.main, 0.1);
      break;
    case 'ready-to-harvest':
      color = theme.palette.success.main;
      bg = alpha(theme.palette.success.main, 0.1);
      break;
    case 'harvested':
      color = theme.palette.text.secondary;
      bg = theme.palette.action.selected;
      break;
    case 'failed':
      color = theme.palette.error.main;
      bg = alpha(theme.palette.error.main, 0.1);
      break;
  }

  return {
    color: color,
    backgroundColor: bg,
    fontWeight: 600,
    textTransform: 'capitalize',
  };
});

// Summary Card Component
const SummaryCard = ({ title, value, icon, color, subtitle }: any) => {
  const theme = useTheme();
  const colorPalette = theme.palette[color as keyof typeof theme.palette] as any;
  const mainColor = colorPalette?.main || theme.palette.primary.main;

  return (
    <Card elevation={0} sx={{ 
      height: '100%', 
      borderRadius: 3, 
      border: `1px solid ${alpha(mainColor, 0.2)}`,
      bgcolor: alpha(mainColor, 0.05),
    }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3, '&:last-child': { pb: 3 } }}>
        <Box sx={{ 
          mr: 2, 
          p: 1.5, 
          borderRadius: 2, 
          bgcolor: alpha(mainColor, 0.15),
          color: mainColor,
          display: 'flex'
        }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight="600">
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold" color="text.primary">
            {value}
          </Typography>
          {subtitle && (
             <Typography variant="caption" color="text.secondary">
               {subtitle}
             </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

/**
 * FarmCrops component for managing crops on a farm
 * Provides functionality to view, add, edit, and delete crop information
 * Integrates with Supabase for backend CRUD
 * Uses shared UI components for consistent UX
 */
const FarmCrops: React.FC<FarmCropsProps> = ({ farm }) => {
  const theme = useTheme();
  if (!farm) return <LoadingState message="Loading farm data..." />;
  const farmId = farm.id;

  // --- State for crops, loading, error, and success feedback ---
  const [crops, setCrops] = useState<Crop[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // --- UI State ---
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCropId, setSelectedCropId] = useState<string | null>(null);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingCrop, setEditingCrop] = useState<Crop | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [newCrop, setNewCrop] = useState<Partial<Crop>>({
    name: '',
    variety: '',
    plantingDate: '',
    harvestDate: '',
    area: 0,
    status: 'planted',
    notes: '',
  });

  // --- Fetch crops from Supabase ---
  const fetchCrops = async () => {
    setLoading(true);
    setError(null);
    const { data, error } = await supabase
      .from('farm_crops')
      .select('*')
      .eq('farm_id', farmId)
      .order('planting_date', { ascending: false });
    if (error) {
      setError('Failed to fetch crops. ' + error.message);
      setCrops([]);
    } else {
      // Map snake_case fields to camelCase for frontend use
      const mapped = (data || []).map((crop: any) => ({
        id: crop.id,
        name: crop.name,
        variety: crop.variety,
        plantingDate: crop.planting_date,
        harvestDate: crop.harvest_date,
        area: crop.area,
        status: crop.status,
        notes: crop.notes,
        expectedYield: crop.expected_yield,
        yieldUnit: crop.yield_unit,
        marketPrice: crop.market_price,
        productionCost: crop.production_cost,
      }));
      setCrops(mapped);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCrops();
    // eslint-disable-next-line
  }, [farmId]);

  // --- Derived State for Filtering ---
  const filteredCrops = crops.filter(crop => {
    const matchesSearch = crop.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          crop.variety?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || crop.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // --- Stats Calculation ---
  const stats = useMemo(() => {
    const total = crops.length;
    const active = crops.filter(c => ['planted', 'growing', 'ready-to-harvest'].includes(c.status)).length;
    const harvested = crops.filter(c => c.status === 'harvested').length;
    const totalArea = crops.reduce((sum, c) => sum + (Number(c.area) || 0), 0);
    
    // Financial Projections
    const projectedRevenue = crops.reduce((sum, c) => sum + ((c.expectedYield || 0) * (c.marketPrice || 0)), 0);
    const totalCost = crops.reduce((sum, c) => sum + ((c.productionCost || 0) * (Number(c.area) || 0)), 0);
    const projectedProfit = projectedRevenue - totalCost;

    // Chart Data Preparation
    // 1. Area Distribution by Variety/Type
    const areaByVariety = crops.reduce((acc: any, c) => {
      const type = c.variety || 'Other';
      acc[type] = (acc[type] || 0) + (Number(c.area) || 0);
      return acc;
    }, {});

    const areaChartData = {
      labels: Object.keys(areaByVariety),
      datasets: [{
        data: Object.values(areaByVariety),
        backgroundColor: [
          theme.palette.primary.main,
          theme.palette.secondary.main,
          theme.palette.success.main,
          theme.palette.warning.main,
          theme.palette.info.main,
          theme.palette.error.main,
        ],
        borderWidth: 1,
      }],
    };

    // 2. Revenue Forecast by Crop
    const revenueByCrop = crops
      .filter(c => (c.expectedYield || 0) * (c.marketPrice || 0) > 0)
      .reduce((acc: any, c) => {
        acc[c.name] = (acc[c.name] || 0) + ((c.expectedYield || 0) * (c.marketPrice || 0));
        return acc;
      }, {});
    
    // Sort by revenue descending
    const sortedRevenue = Object.entries(revenueByCrop)
      .sort(([, a]: any, [, b]: any) => b - a)
      .slice(0, 5); // Top 5

    const revenueChartData = {
      labels: sortedRevenue.map(([name]) => name),
      datasets: [{
        label: 'Projected Revenue ($)',
        data: sortedRevenue.map(([, val]) => val),
        backgroundColor: theme.palette.success.light,
        borderColor: theme.palette.success.main,
        borderWidth: 1,
        borderRadius: 4,
      }],
    };

    return { 
      total, 
      active, 
      harvested, 
      totalArea, 
      projectedRevenue, 
      totalCost,
      projectedProfit,
      areaChartData,
      revenueChartData
    };
  }, [crops, theme]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, cropId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedCropId(cropId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCropId(null);
  };

  const handleEditFromMenu = () => {
    const crop = crops.find(c => c.id === selectedCropId);
    if (crop) handleEditCrop(crop);
    handleMenuClose();
  };

  const handleDeleteFromMenu = () => {
    if (selectedCropId) handleDeleteCrop(selectedCropId);
    handleMenuClose();
  };

  const calculateProgress = (start: string, end: string) => {
    if (!start || !end) return 0;
    const startDate = dayjs(start).valueOf();
    const endDate = dayjs(end).valueOf();
    const now = dayjs().valueOf();
    const total = endDate - startDate;
    const elapsed = now - startDate;
    if (total <= 0) return 0;
    const progress = (elapsed / total) * 100;
    return Math.min(Math.max(progress, 0), 100);
  };

  /**
   * Opens the dialog to add a new crop
   * Initializes the editing crop state with default values
   */
  const handleAddCrop = () => {
    setEditingCrop(null);
    setNewCrop({
      name: '',
      variety: '',
      plantingDate: '',
      harvestDate: '',
      area: 0,
      status: 'planted',
      notes: '',
    });
    setOpenDialog(true);
    setError(null);
  };

  /**
   * Opens the dialog to edit an existing crop
   * @param crop - The crop object to edit
   */
  const handleEditCrop = (crop: Crop) => {
    setEditingCrop(crop);
    setNewCrop(crop);
    setOpenDialog(true);
    setError(null);
  };

  /**
   * Deletes a crop from the crops list and Supabase
   * @param cropId - The ID of the crop to delete
   */
  const handleDeleteCrop = async (cropId: string) => {
    setLoading(true);
    setError(null);
    const { error } = await supabase.from('farm_crops').delete().eq('id', cropId);
    if (error) {
      setError('Failed to delete crop.');
    } else {
      setSuccess('Crop deleted successfully.');
      await fetchCrops();
    }
    setLoading(false);
  };

  /**
   * Saves the current crop being edited (add or update in Supabase)
   */
  const handleSaveCrop = async () => {
    const errors: { [key: string]: string } = {};
    if (!newCrop.name) errors.name = 'Crop name is required';
    if (!newCrop.variety) errors.variety = 'Type is required';
    if (!newCrop.plantingDate) errors.plantingDate = 'Planting date is required';
    if (!newCrop.status) errors.status = 'Status is required';
    if (!newCrop.area || Number(newCrop.area) <= 0) errors.area = 'Area must be greater than 0';
    if ((newCrop.expectedYield ?? 0) < 0) errors.expectedYield = 'Expected yield cannot be negative';
    if ((newCrop.marketPrice ?? 0) < 0) errors.marketPrice = 'Price cannot be negative';
    if ((newCrop.productionCost ?? 0) < 0) errors.productionCost = 'Cost per acre cannot be negative';
    if ((newCrop.expectedYield ?? null) !== null && (newCrop.expectedYield as number) > 0 && !newCrop.yieldUnit) {
      errors.yieldUnit = 'Select a yield unit';
    }
    if (newCrop.yieldUnit && !(newCrop.marketPrice && newCrop.marketPrice > 0)) {
      errors.marketPrice = `Enter market price per ${newCrop.yieldUnit}`;
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fill in all required fields.');
      return;
    }
    setLoading(true);
    setError(null);
    if (editingCrop) {
      // Update crop
      const { error } = await supabase
        .from('farm_crops')
        .update({
          name: newCrop.name,
          variety: newCrop.variety,
          planting_date: newCrop.plantingDate,
          harvest_date: newCrop.harvestDate,
          area: newCrop.area,
          status: newCrop.status,
          notes: newCrop.notes,
          expected_yield: newCrop.expectedYield,
          yield_unit: newCrop.yieldUnit,
          market_price: newCrop.marketPrice,
          production_cost: newCrop.productionCost,
          farm_id: farmId,
        })
        .eq('id', editingCrop.id);
      if (error) {
        setError('Failed to update crop. ' + error.message);
      } else {
        setSuccess('Crop updated successfully.');
        setOpenDialog(false);
        await fetchCrops();
      }
    } else {
      // Add crop
      const { error } = await supabase
        .from('farm_crops')
        .insert([
          {
            name: newCrop.name,
            variety: newCrop.variety,
            planting_date: newCrop.plantingDate,
            harvest_date: newCrop.harvestDate,
            area: newCrop.area,
            status: newCrop.status,
            notes: newCrop.notes,
            expected_yield: newCrop.expectedYield,
            yield_unit: newCrop.yieldUnit,
            market_price: newCrop.marketPrice,
            production_cost: newCrop.productionCost,
            farm_id: farmId,
          },
        ]);
      if (error) {
        setError('Failed to add crop. ' + error.message);
      } else {
        setSuccess('Crop added successfully.');
    setOpenDialog(false);
        await fetchCrops();
      }
    }
    setLoading(false);
  };

  /**
   * Handles changes to crop form input fields
   * @param field - The field name to update
   * @param value - The new value for the field
   */
  const handleInputChange = (field: keyof Crop, value: string | number) => {
    setNewCrop({ ...newCrop, [field]: value });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Success Alert */}
      {success && (
        <AppAlert severity="success" message={success} onClose={() => setSuccess(null)} />
      )}
      {/* Error Alert */}
      {error && (
        <AppAlert severity="error" message={error} onClose={() => setError(null)} />
      )}
      
      {/* Header & Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard 
            title="Total Crops" 
            value={stats.total} 
            icon={<AgricultureIcon />} 
            color="primary" 
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard 
            title="Active Growing" 
            value={stats.active} 
            icon={<GrassIcon />} 
            color="success" 
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard 
            title="Harvested" 
            value={stats.harvested} 
            icon={<CheckCircleIcon />} 
            color="info" 
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard 
            title="Total Area" 
            value={`${stats.totalArea} acres`} 
            icon={<TerrainIcon />} 
            color="warning" 
          />
        </Grid>
      </Grid>

      {/* Financial Projections */}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>Financial Overview</Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <SummaryCard 
            title="Projected Revenue" 
            value={`$${stats.projectedRevenue.toLocaleString()}`} 
            icon={<PaidIcon />} 
            color="success"
            subtitle="Based on expected yield & market price"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <SummaryCard 
            title="Production Cost" 
            value={`$${stats.totalCost.toLocaleString()}`} 
            icon={<OpacityIcon />} 
            color="error"
            subtitle="Seeds, fertilizers, labor estimates"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <SummaryCard 
            title="Est. Net Margin" 
            value={`$${stats.projectedProfit.toLocaleString()}`} 
            icon={<AccountBalanceIcon />} // Need to import this or reuse another
            color="primary"
            subtitle={`${stats.projectedRevenue > 0 ? ((stats.projectedProfit / stats.projectedRevenue) * 100).toFixed(1) : 0}% Margin`}
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: '16px', height: '100%', minHeight: 350 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Revenue Forecast by Crop</Typography>
            <Box sx={{ height: 300 }}>
              <Bar 
                data={stats.revenueChartData} 
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: { y: { beginAtZero: true } }
                }} 
              />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: '16px', height: '100%', minHeight: 350 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Area Distribution</Typography>
            <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
              <Doughnut 
                data={stats.areaChartData} 
                options={{
                  maintainAspectRatio: false,
                  plugins: { legend: { position: 'bottom' } }
                }} 
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Toolbar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <TextField
                placeholder="Search crops..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon color="action" />
                        </InputAdornment>
                    ),
                }}
                sx={{ 
                    '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        width: { xs: '100%', sm: 300 } 
                    } 
                }}
            />
            <FormControl size="small" sx={{ minWidth: 150, bgcolor: 'background.paper', borderRadius: 2 }}>
                <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    displayEmpty
                    sx={{ borderRadius: 2 }}
                >
                    <MenuItem value="all">All Statuses</MenuItem>
                    {cropStatuses.map(s => (
                        <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newView) => { if(newView) setViewMode(newView); }}
                size="small"
                sx={{ bgcolor: 'background.paper' }}
            >
                <ToggleButton value="grid"><ViewModuleIcon /></ToggleButton>
                <ToggleButton value="list"><ViewListIcon /></ToggleButton>
            </ToggleButtonGroup>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddCrop}
          sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
        >
          Add Crop
        </Button>
      </Box>

      {/* Loading State */}
      {loading && <LoadingState message="Loading crops..." />}

      {/* Empty State */}
      {!loading && filteredCrops.length === 0 && (
        <EmptyState
          icon={<AddCircleOutlineOutlinedIcon fontSize="inherit" color="primary" />}
          title={searchQuery ? "No matching crops found" : "No crops yet"}
          description={searchQuery ? "Try adjusting your filters" : "Get started by adding your first crop."}
          action={
            !searchQuery ? (
                <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddCrop}>
                  Add Crop
                </Button>
            ) : undefined
          }
        />
      )}

      {/* Crops Content */}
      {!loading && filteredCrops.length > 0 && (
        viewMode === 'list' ? (
          <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Table>
              <TableHead sx={{ bgcolor: theme.palette.mode === 'light' ? '#f8f9fa' : 'rgba(255,255,255,0.05)' }}>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Planting Date</TableCell>
                  <TableCell>Harvest Date</TableCell>
                  <TableCell>Area (acres)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCrops.map((crop) => (
                  <TableRow key={crop.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{crop.name}</TableCell>
                    <TableCell>{crop.variety}</TableCell>
                    <TableCell>{dayjs(crop.plantingDate).format('MMM D, YYYY')}</TableCell>
                  <TableCell>{crop.harvestDate ? dayjs(crop.harvestDate).format('MMM D, YYYY') : 'N/A'}</TableCell>
                    <TableCell>{crop.area}</TableCell>
                    <TableCell>
                        <StatusChip label={crop.status} status={crop.status} size="small" />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEditCrop(crop)} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteCrop(crop.id)} size="small" color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
            <Grid container spacing={3}>
                {filteredCrops.map((crop) => {
                    const progress = calculateProgress(crop.plantingDate, crop.harvestDate);
                    return (
                        <Grid item xs={12} sm={6} md={4} key={crop.id}>
                            <StyledCard elevation={2}>
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                        <Box display="flex" gap={2}>
                                            <Box sx={{ 
                                                width: 48, 
                                                height: 48, 
                                                borderRadius: 2, 
                                                bgcolor: 'primary.light', 
                                                color: 'primary.main',
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center' 
                                            }}>
                                                <GrassIcon />
                                            </Box>
                                            <Box>
                                                <Typography variant="h6" fontWeight="bold">{crop.name}</Typography>
                                                <Typography variant="body2" color="textSecondary">{crop.variety}</Typography>
                                            </Box>
                                        </Box>
                                        <IconButton size="small" onClick={(e) => handleMenuClick(e, crop.id)}>
                                            <MoreVertIcon />
                                        </IconButton>
                                    </Box>
                                    
                                    <Box mb={2}>
                                        <StatusChip label={crop.status} status={crop.status} size="small" />
                                    </Box>

                                    <Box mb={2}>
                                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                                            <Typography variant="caption" color="textSecondary">Growth Progress</Typography>
                                            <Typography variant="caption" fontWeight="bold">{Math.round(progress)}%</Typography>
                                        </Box>
                                        <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3 }} />
                                    </Box>

                                    <Grid container spacing={1} sx={{ mt: 1 }}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" display="block" color="textSecondary">Planted</Typography>
                                            <Typography variant="body2">{dayjs(crop.plantingDate).format('MMM D, YYYY')}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" display="block" color="textSecondary">Area</Typography>
                                            <Typography variant="body2">{crop.area} acres</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" display="block" color="textSecondary">Proj. Revenue</Typography>
                                            <Typography variant="body2" fontWeight="bold" color="success.main">
                                                ${((crop.expectedYield || 0) * (crop.marketPrice || 0)).toLocaleString()}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" display="block" color="textSecondary">Prod. Cost</Typography>
                                            <Typography variant="body2" color="error.main">
                                                ${(((crop.productionCost || 0) * (Number(crop.area) || 0))).toLocaleString()}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" display="block" color="textSecondary">Net Margin</Typography>
                                            <Typography variant="body2" fontWeight="bold" color={(((crop.expectedYield || 0) * (crop.marketPrice || 0)) - ((crop.productionCost || 0) * (Number(crop.area) || 0))) >= 0 ? 'primary.main' : 'error.main'}>
                                                ${(((crop.expectedYield || 0) * (crop.marketPrice || 0) - ((crop.productionCost || 0) * (Number(crop.area) || 0))).toLocaleString())}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </StyledCard>
                        </Grid>
                    );
                })}
            </Grid>
        )
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ sx: { minWidth: 150, borderRadius: 2 } }}
      >
        <MenuItem onClick={handleEditFromMenu}>
            <EditIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteFromMenu} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCrop ? 'Edit Crop' : 'Add New Crop'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Crop Name"
                value={newCrop.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Type"
                value={newCrop.variety}
                onChange={(e) => handleInputChange('variety', e.target.value)}
                required
              >
                {cropTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Planting Date"
                value={newCrop.plantingDate}
                onChange={(e) => handleInputChange('plantingDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={!!fieldErrors.plantingDate}
                helperText={fieldErrors.plantingDate}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Harvest Date"
                value={newCrop.harvestDate}
                onChange={(e) => handleInputChange('harvestDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Area (acres)"
                type="number"
                value={newCrop.area}
                onChange={(e) => handleInputChange('area', parseFloat(e.target.value))}
                inputProps={{ min: 0 }}
                error={!!fieldErrors.area}
                helperText={fieldErrors.area}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 2, mt: 1, color: 'text.secondary' }}>Financial Projections</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    select
                    label="Yield Unit"
                    value={newCrop.yieldUnit || ''}
                    onChange={(e) => handleInputChange('yieldUnit', e.target.value)}
                    error={!!fieldErrors.yieldUnit}
                    helperText={fieldErrors.yieldUnit || 'Choose the unit for expected yield'}
                  >
                    {yieldUnits.map((u) => (
                      <MenuItem key={u} value={u}>{u}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    label="Exp. Yield"
                    type="number"
                    value={newCrop.expectedYield || ''}
                    onChange={(e) => handleInputChange('expectedYield', parseFloat(e.target.value))}
                    inputProps={{ min: 0 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {newCrop.yieldUnit || 'unit'}
                        </InputAdornment>
                      )
                    }}
                    error={!!fieldErrors.expectedYield}
                    helperText={fieldErrors.expectedYield || (newCrop.yieldUnit ? `Total expected yield in ${newCrop.yieldUnit}` : 'Select a yield unit to clarify quantity')}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    label="Mkt Price ($)"
                    type="number"
                    value={newCrop.marketPrice || ''}
                    onChange={(e) => handleInputChange('marketPrice', parseFloat(e.target.value))}
                    inputProps={{ min: 0 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      endAdornment: (
                        <InputAdornment position="end">
                          {newCrop.yieldUnit ? `/ ${newCrop.yieldUnit}` : '/ unit'}
                        </InputAdornment>
                      )
                    }}
                    error={!!fieldErrors.marketPrice}
                    helperText={fieldErrors.marketPrice || (newCrop.yieldUnit ? `Price per ${newCrop.yieldUnit}` : 'Set a unit above to clarify pricing')}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    label="Cost ($)"
                    type="number"
                    value={newCrop.productionCost || ''}
                    onChange={(e) => handleInputChange('productionCost', parseFloat(e.target.value))}
                    inputProps={{ min: 0 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      endAdornment: <InputAdornment position="end">/ acre</InputAdornment>,
                    }}
                    error={!!fieldErrors.productionCost}
                    helperText={fieldErrors.productionCost || 'Cost per acre'}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Status"
                value={newCrop.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                error={!!fieldErrors.status}
                helperText={fieldErrors.status}
                required
              >
                {cropStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={newCrop.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveCrop} variant="contained" color="primary">
            {editingCrop ? 'Update' : 'Add'} Crop
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FarmCrops;
